import { type FormEvent } from 'react';
import { deleteFetcher, postFetcher, patchFetcher } from '@/api/fetchers';
import {
  goodNotification,
  badNotification,
} from '@/components/Notification/Notification';
import { displayError } from '@/utils/error/errorHandler';

const PlanningActionHandler = () => {
  const changeClientPlanState = async (
    event: FormEvent<HTMLFormElement>,
    clientPlanId: number | undefined,
    message: string,
    state: string,
    toggle: () => void
  ) => {
    event.preventDefault();
    if (!clientPlanId) return;
    try {
      await patchFetcher(`/client-plans/${clientPlanId}`, { state });
      goodNotification(message);
      toggle();
    } catch (err) {
      displayError(err);
    }
  };

  const handleToForPlanningState = async (
    event: FormEvent<HTMLFormElement>,
    clientPlanId: number | undefined,
    toggle: () => void
  ) => {
    const message = 'Měsíční plán klienta byl přesunut do sekce K doplánování.';
    const state = 'TO_PLAN';
    changeClientPlanState(event, clientPlanId, message, state, toggle);
  };

  const handleToPlannedState = async (
    event: FormEvent<HTMLFormElement>,
    clientPlanId: number | undefined,
    toggle: () => void
  ) => {
    const message = 'Měsíční plán klienta byl přesunut do sekce Naplánováno.';
    const state = 'PLANNED';
    changeClientPlanState(event, clientPlanId, message, state, toggle);
  };

  const handleToPlanningState = async (
    event: FormEvent<HTMLFormElement>,
    clientPlanId: number | undefined,
    toggle: () => void
  ) => {
    const message = 'Měsíční plán klienta byl přesunut do sekce Plánování.';
    const state = 'NOT_PLANNED';
    changeClientPlanState(event, clientPlanId, message, state, toggle);
  };

  const cancelAllAssignments = async (
    event: FormEvent<HTMLFormElement>,
    clientPlanId: number | undefined,
    toggle: () => void
  ) => {
    event.preventDefault();
    if (!clientPlanId) return;
    try {
      await deleteFetcher(`/client-plans/${clientPlanId}/assignments`);
      goodNotification('Všechna přiřazení byla smazána.');
      toggle();
    } catch (err) {
      displayError(err);
    }
  };

  const deleteTask = async (
    clientPlanId: number | undefined,
    taskId: number | undefined
  ) => {
    if (!clientPlanId || !taskId) return;
    try {
      await deleteFetcher(`/client-plans/${clientPlanId}/tasks/${taskId}`);
      goodNotification('Úkol by smazán.');
    } catch (err) {
      displayError(err);
    }
  };

  const deleteTaskGroup = async (
    clientPlanId: number | undefined,
    groupId: number | undefined
  ) => {
    if (!clientPlanId || !groupId) return;
    try {
      await deleteFetcher(
        `/client-plans/${clientPlanId}/task-groups/${groupId}`
      );
      goodNotification('Skupina úkolů byla smazána.');
    } catch (err) {
      displayError(err);
    }
  };

  const cancelAllEmployeeGroupAssignments = async (
    clientPlanId: number | undefined,
    groupId: number | undefined
  ) => {
    if (!clientPlanId) return;
    try {
      await deleteFetcher(
        `/client-plans/${clientPlanId}/task-groups/${groupId}/assignments`
      );
      goodNotification(
        'Všechna přiřazení zaměstnance pro tuto skupinu úkolů byla zrušena.'
      );
    } catch (err) {
      displayError(err);
    }
  };

  const assignAllPossibleGroupTaskAssignments = async (
    clientPlanId: number | undefined,
    groupId: number | undefined,
    emps: number[],
    assignAvailableOnly: boolean
  ) => {
    if (!clientPlanId) return;
    try {
      await postFetcher(
        `/client-plans/${clientPlanId}/task-groups/${groupId}/assignments`,
        {
          emps,
          assignAvailableOnly,
        }
      );
      goodNotification(
        'Zaměstnanec byl přiřazen ke všem úkolům, pro který byl dostupný.'
      );
    } catch (err) {
      displayError(err);
    }
  };

  const lockPlan = async (
    event: FormEvent<HTMLFormElement>,
    serviceId: number | undefined,
    planId: number | undefined,
    locked: boolean,
    toggle: () => void
  ) => {
    event.preventDefault();
    if (!serviceId || !planId) return;
    try {
      await patchFetcher(`/services/${serviceId}/plans/${planId}`, {
        locked,
      });
      goodNotification(`Plán byl úspěšně ${locked ? 'uzamčen' : 'odemčen'}.`);
      toggle();
    } catch (err) {
      displayError(err);
    }
  };

  const regenerateBillingOrReportForGivenId = async (
    billingOrReportId: number,
    keepManualRec: boolean,
    type: 'billing' | 'report'
  ) => {
    try {
      await postFetcher(`/${type}s/${billingOrReportId}/regeneration`, {
        keepManualRec,
      });
      goodNotification('Přegenerování proběhlo úspěšně.');
    } catch (err) {
      displayError(err);
    }
  };

  const regenerateBillingsOrReports = async (
    planId: number,
    keepManualRec: boolean,
    type: 'billing' | 'report'
  ) => {
    try {
      await postFetcher(`/${type}s/regeneration`, {
        planId,
        keepManualRec,
      });
      goodNotification('Přegenerování proběhlo úspěšně.');
    } catch (err) {
      displayError(err);
    }
  };

  const updateBillingOrReportLockStatus = async (
    billingOrReportId: number,
    type: 'billing' | 'report',
    locked: boolean = true
  ) => {
    try {
      await patchFetcher(`/${type}s`, {
        locked,
        planId: billingOrReportId,
      });
      goodNotification(
        `Výkaz byl úspěšně ${locked ? 'uzamknut' : 'odemknut'}.`
      );
    } catch (err) {
      displayError(err);
    }
  };

  const dispatchBillingOrReport = async (
    planId: number,
    type: 'billing' | 'report'
  ) => {
    try {
      await postFetcher(`/dispatches/plans/${planId}/${type}s`, {});
      goodNotification('Výkaz byl úspěšně odeslán.');
    } catch (err) {
      displayError(err);
    }
  };

  const dispatchBillingOrReportGivenIds = async (
    planId: number,
    type: 'billing' | 'report',
    ids: [number]
  ) => {
    try {
      await postFetcher(`/dispatches/plans/${planId}/${type}s`, { ids });
      goodNotification('Výkaz byl úspěšně odeslán.');
    } catch (err) {
      displayError(err);
    }
  };

  const exportBillingOrReportGivenIds = async (
    planId: number,
    type: 'billing' | 'report',
    ids: [number]
  ) => {
    try {
      await postFetcher(`/exports/plans/${planId}/${type}s`, { ids });
      goodNotification('Výkaz byl úspěšně stažen.');
    } catch (err) {
      displayError(err);
    }
  };

  const noClientPlanSelected = (clientPlanId: number | undefined) => {
    if (!clientPlanId) {
      badNotification('Nejprve je nutné vybrat plán klienta!');
    }
  };

  return {
    handleToForPlanningState,
    handleToPlannedState,
    handleToPlanningState,
    cancelAllAssignments,
    lockPlan,
    regenerateBillingOrReportForGivenId,
    regenerateBillingsOrReports,
    updateBillingOrReportLockStatus,
    dispatchBillingOrReport,
    noClientPlanSelected,
    dispatchBillingOrReportGivenIds,
    exportBillingOrReportGivenIds,
    deleteTaskGroup,
    deleteTask,
    cancelAllEmployeeGroupAssignments,
    assignAllPossibleGroupTaskAssignments,
  };
};

export default PlanningActionHandler;
